import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { 
  BookOpen, ArrowLeft, Clock, MapPin, User, Star, Users, 
  CheckCircle, Lightbulb, Target, GraduationCap, UserCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

// JHS Subject Data
const jhsSubjectsData = {
  mathematics: {
    name: "Mathematics",
    color: "from-[#002147] to-[#003366]",
    icon: "🔢",
    room: "JHS Block, Room 101",
    schedule: "Mon-Fri 8:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Advanced mathematical concepts preparing students for senior high school and beyond.",
    fullDescription: "The JHS Mathematics program builds on primary school foundations to develop advanced mathematical skills. Students master algebra, geometry, statistics, and problem-solving techniques essential for BECE success.",
    objectives: [
      "Master algebraic expressions and equations",
      "Understand advanced geometry and trigonometry basics",
      "Analyze and interpret statistical data",
      "Solve complex word problems",
      "Apply mathematics to real-world situations",
      "Prepare thoroughly for BECE examinations"
    ],
    activities: [
      "Advanced problem-solving sessions",
      "Math olympiad preparation",
      "Statistical analysis projects",
      "BECE past question practice",
      "Group collaborative learning",
      "Mathematical modeling"
    ],
    materials: ["JHS Math textbooks", "Scientific calculators", "Geometry sets", "Graph books", "BECE prep materials"],
    rating: 4.8,
    enrolled: 90,
    resourceLinks: [
      { name: "Resource 1", url: "#jhs-math-1" },
      { name: "Resource 2", url: "#jhs-math-2" },
      { name: "Resource 3", url: "#jhs-math-3" },
      { name: "Resource 4", url: "#jhs-math-4" },
      { name: "Resource 5", url: "#jhs-math-5" },
      { name: "Resource 6", url: "#jhs-math-6" },
      { name: "Resource 7", url: "#jhs-math-7" },
      { name: "Resource 8", url: "#jhs-math-8" },
      { name: "Resource 9", url: "#jhs-math-9" },
      { name: "Resource 10", url: "#jhs-math-10" },
      { name: "Homework 1", url: "#jhs-math-hw-1" },
      { name: "Homework 2", url: "#jhs-math-hw-2" },
      { name: "Homework 3", url: "#jhs-math-hw-3" },
      { name: "Homework 4", url: "#jhs-math-hw-4" },
      { name: "Homework 5", url: "#jhs-math-hw-5" },
      { name: "Homework 6", url: "#jhs-math-hw-6" },
      { name: "Homework 7", url: "#jhs-math-hw-7" },
      { name: "Homework 8", url: "#jhs-math-hw-8" },
      { name: "Homework 9", url: "#jhs-math-hw-9" },
      { name: "Homework 10", url: "#jhs-math-hw-10" },
    ]
  },
  "english-language": {
    name: "English Language",
    color: "from-[#002147] to-[#003366]",
    icon: "📖",
    room: "JHS Block, Room 102",
    schedule: "Mon-Fri 9:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Comprehensive English skills for effective communication and academic excellence.",
    fullDescription: "The JHS English Language program develops advanced literacy skills for academic success. Students master essay writing, comprehension, summary writing, and oral English for BECE preparation.",
    objectives: [
      "Write excellent essays and compositions",
      "Master summary and comprehension techniques",
      "Develop advanced vocabulary and grammar",
      "Excel in oral English examinations",
      "Analyze literary texts critically",
      "Achieve distinction in BECE English"
    ],
    activities: [
      "Essay writing workshops",
      "Comprehension and summary practice",
      "Debates and public speaking",
      "Literature circle discussions",
      "Grammar clinics",
      "BECE preparation drills"
    ],
    materials: ["JHS English textbooks", "Literature set texts", "Grammar guides", "Dictionaries", "BECE prep books"],
    rating: 4.9,
    enrolled: 90,
    resourceLinks: [
      { name: "Resource 1", url: "#jhs-eng-1" },
      { name: "Resource 2", url: "#jhs-eng-2" },
      { name: "Resource 3", url: "#jhs-eng-3" },
      { name: "Resource 4", url: "#jhs-eng-4" },
      { name: "Resource 5", url: "#jhs-eng-5" },
      { name: "Resource 6", url: "#jhs-eng-6" },
      { name: "Resource 7", url: "#jhs-eng-7" },
      { name: "Resource 8", url: "#jhs-eng-8" },
      { name: "Resource 9", url: "#jhs-eng-9" },
      { name: "Resource 10", url: "#jhs-eng-10" },
      { name: "Homework 1", url: "#jhs-eng-hw-1" },
      { name: "Homework 2", url: "#jhs-eng-hw-2" },
      { name: "Homework 3", url: "#jhs-eng-hw-3" },
      { name: "Homework 4", url: "#jhs-eng-hw-4" },
      { name: "Homework 5", url: "#jhs-eng-hw-5" },
      { name: "Homework 6", url: "#jhs-eng-hw-6" },
      { name: "Homework 7", url: "#jhs-eng-hw-7" },
      { name: "Homework 8", url: "#jhs-eng-hw-8" },
      { name: "Homework 9", url: "#jhs-eng-hw-9" },
      { name: "Homework 10", url: "#jhs-eng-hw-10" },
    ]
  },
  "integrated-science": {
    name: "Integrated Science",
    color: "from-[#002147] to-[#003366]",
    icon: "🔬",
    room: "Science Laboratory",
    schedule: "Mon/Wed/Fri 10:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Integrated approach to biology, chemistry, and physics for scientific literacy.",
    fullDescription: "The Integrated Science program combines biology, chemistry, and physics concepts. Students develop scientific inquiry skills through laboratory experiments and theoretical understanding.",
    objectives: [
      "Understand biological systems and life processes",
      "Learn chemical reactions and properties of matter",
      "Explore physics principles and energy",
      "Conduct laboratory experiments safely",
      "Apply scientific method to investigations",
      "Excel in BECE Integrated Science"
    ],
    activities: [
      "Laboratory practicals",
      "Scientific investigations",
      "Science fair projects",
      "Field studies",
      "BECE practical preparation",
      "Research presentations"
    ],
    materials: ["Integrated Science textbooks", "Lab equipment", "Safety gear", "Specimen collections", "BECE prep materials"],
    rating: 4.7,
    enrolled: 90,
    resourceLinks: [
      { name: "Resource 1", url: "#jhs-sci-1" },
      { name: "Resource 2", url: "#jhs-sci-2" },
      { name: "Resource 3", url: "#jhs-sci-3" },
      { name: "Resource 4", url: "#jhs-sci-4" },
      { name: "Resource 5", url: "#jhs-sci-5" },
      { name: "Resource 6", url: "#jhs-sci-6" },
      { name: "Resource 7", url: "#jhs-sci-7" },
      { name: "Resource 8", url: "#jhs-sci-8" },
      { name: "Resource 9", url: "#jhs-sci-9" },
      { name: "Resource 10", url: "#jhs-sci-10" },
      { name: "Homework 1", url: "#jhs-sci-hw-1" },
      { name: "Homework 2", url: "#jhs-sci-hw-2" },
      { name: "Homework 3", url: "#jhs-sci-hw-3" },
      { name: "Homework 4", url: "#jhs-sci-hw-4" },
      { name: "Homework 5", url: "#jhs-sci-hw-5" },
      { name: "Homework 6", url: "#jhs-sci-hw-6" },
      { name: "Homework 7", url: "#jhs-sci-hw-7" },
      { name: "Homework 8", url: "#jhs-sci-hw-8" },
      { name: "Homework 9", url: "#jhs-sci-hw-9" },
      { name: "Homework 10", url: "#jhs-sci-hw-10" },
    ]
  },
  "social-studies": {
    name: "Social Studies",
    color: "from-[#002147] to-[#003366]",
    icon: "🌍",
    room: "JHS Block, Room 103",
    schedule: "Tue/Thu 10:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Understanding society, governance, environment, and responsible citizenship.",
    fullDescription: "The Social Studies program develops students' understanding of society, government, and citizenship. Students learn about Ghana's constitution, environmental issues, and social responsibilities.",
    objectives: [
      "Understand Ghana's constitution and governance",
      "Learn about social institutions and their roles",
      "Analyze environmental and population issues",
      "Develop responsible citizenship values",
      "Understand economic concepts and development",
      "Prepare comprehensively for BECE"
    ],
    activities: [
      "Civic education projects",
      "Environmental awareness campaigns",
      "Mock parliament sessions",
      "Community mapping exercises",
      "Current affairs discussions",
      "BECE question practice"
    ],
    materials: ["Social Studies textbooks", "Ghana's Constitution", "Maps and atlases", "Current affairs materials", "BECE prep books"],
    rating: 4.6,
    enrolled: 90,
    resourceLinks: [
      { name: "Resource 1", url: "#jhs-social-1" },
      { name: "Resource 2", url: "#jhs-social-2" },
      { name: "Resource 3", url: "#jhs-social-3" },
      { name: "Resource 4", url: "#jhs-social-4" },
      { name: "Resource 5", url: "#jhs-social-5" },
      { name: "Resource 6", url: "#jhs-social-6" },
      { name: "Resource 7", url: "#jhs-social-7" },
      { name: "Resource 8", url: "#jhs-social-8" },
      { name: "Resource 9", url: "#jhs-social-9" },
      { name: "Resource 10", url: "#jhs-social-10" },
      { name: "Homework 1", url: "#jhs-social-hw-1" },
      { name: "Homework 2", url: "#jhs-social-hw-2" },
      { name: "Homework 3", url: "#jhs-social-hw-3" },
      { name: "Homework 4", url: "#jhs-social-hw-4" },
      { name: "Homework 5", url: "#jhs-social-hw-5" },
      { name: "Homework 6", url: "#jhs-social-hw-6" },
      { name: "Homework 7", url: "#jhs-social-hw-7" },
      { name: "Homework 8", url: "#jhs-social-hw-8" },
      { name: "Homework 9", url: "#jhs-social-hw-9" },
      { name: "Homework 10", url: "#jhs-social-hw-10" },
    ]
  },
  rme: {
    name: "Religious and Moral Education (RME)",
    color: "from-[#002147] to-[#003366]",
    icon: "✝️",
    room: "JHS Block, Room 104",
    schedule: "Tue/Thu 11:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Comparative religious studies and moral philosophy for ethical decision-making.",
    fullDescription: "The JHS RME program provides in-depth study of Christianity, Islam, and Traditional African Religion. Students develop critical thinking about moral issues and ethical decision-making.",
    objectives: [
      "Compare and contrast major world religions",
      "Analyze moral issues from multiple perspectives",
      "Understand religious practices and their significance",
      "Apply ethical principles to life situations",
      "Develop tolerance and respect for all beliefs",
      "Excel in BECE RME examinations"
    ],
    activities: [
      "Comparative religion studies",
      "Ethical case study analyses",
      "Interfaith dialogue sessions",
      "Moral philosophy discussions",
      "Community service projects",
      "BECE preparation sessions"
    ],
    materials: ["RME textbooks", "Religious scriptures", "Ethics resources", "Case study materials", "BECE prep books"],
    rating: 4.8,
    enrolled: 90,
    resourceLinks: [
      { name: "Resource 1", url: "#jhs-rme-1" },
      { name: "Resource 2", url: "#jhs-rme-2" },
      { name: "Resource 3", url: "#jhs-rme-3" },
      { name: "Resource 4", url: "#jhs-rme-4" },
      { name: "Resource 5", url: "#jhs-rme-5" },
      { name: "Resource 6", url: "#jhs-rme-6" },
      { name: "Resource 7", url: "#jhs-rme-7" },
      { name: "Resource 8", url: "#jhs-rme-8" },
      { name: "Resource 9", url: "#jhs-rme-9" },
      { name: "Resource 10", url: "#jhs-rme-10" },
      { name: "Homework 1", url: "#jhs-rme-hw-1" },
      { name: "Homework 2", url: "#jhs-rme-hw-2" },
      { name: "Homework 3", url: "#jhs-rme-hw-3" },
      { name: "Homework 4", url: "#jhs-rme-hw-4" },
      { name: "Homework 5", url: "#jhs-rme-hw-5" },
      { name: "Homework 6", url: "#jhs-rme-hw-6" },
      { name: "Homework 7", url: "#jhs-rme-hw-7" },
      { name: "Homework 8", url: "#jhs-rme-hw-8" },
      { name: "Homework 9", url: "#jhs-rme-hw-9" },
      { name: "Homework 10", url: "#jhs-rme-hw-10" },
    ]
  },
  "creative-arts": {
    name: "Creative Arts",
    color: "from-[#002147] to-[#003366]",
    icon: "🎨",
    room: "Art and Music Studio",
    schedule: "Wed/Fri 11:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Advanced visual and performing arts for creative expression and cultural appreciation.",
    fullDescription: "The JHS Creative Arts program develops advanced artistic skills in visual arts, music, and drama. Students create portfolios, perform in concerts, and appreciate diverse art forms.",
    objectives: [
      "Master advanced art techniques and media",
      "Compose and perform music confidently",
      "Create and perform dramatic works",
      "Develop personal artistic style",
      "Appreciate Ghanaian and world art",
      "Build comprehensive art portfolios"
    ],
    activities: [
      "Advanced studio practice",
      "Music ensemble performances",
      "Drama productions",
      "Art exhibitions",
      "Cultural festivals",
      "Portfolio development"
    ],
    materials: ["Advanced art supplies", "Musical instruments", "Drama costumes", "Portfolio materials", "Art history resources"],
    rating: 4.9,
    enrolled: 90,
    resourceLinks: [
      { name: "Resource 1", url: "#jhs-arts-1" },
      { name: "Resource 2", url: "#jhs-arts-2" },
      { name: "Resource 3", url: "#jhs-arts-3" },
      { name: "Resource 4", url: "#jhs-arts-4" },
      { name: "Resource 5", url: "#jhs-arts-5" },
      { name: "Resource 6", url: "#jhs-arts-6" },
      { name: "Resource 7", url: "#jhs-arts-7" },
      { name: "Resource 8", url: "#jhs-arts-8" },
      { name: "Resource 9", url: "#jhs-arts-9" },
      { name: "Resource 10", url: "#jhs-arts-10" },
      { name: "Homework 1", url: "#jhs-arts-hw-1" },
      { name: "Homework 2", url: "#jhs-arts-hw-2" },
      { name: "Homework 3", url: "#jhs-arts-hw-3" },
      { name: "Homework 4", url: "#jhs-arts-hw-4" },
      { name: "Homework 5", url: "#jhs-arts-hw-5" },
      { name: "Homework 6", url: "#jhs-arts-hw-6" },
      { name: "Homework 7", url: "#jhs-arts-hw-7" },
      { name: "Homework 8", url: "#jhs-arts-hw-8" },
      { name: "Homework 9", url: "#jhs-arts-hw-9" },
      { name: "Homework 10", url: "#jhs-arts-hw-10" },
    ]
  },
  "career-technology": {
    name: "Career Technology",
    color: "from-[#002147] to-[#003366]",
    icon: "🔧",
    room: "Technical Workshop",
    schedule: "Mon/Wed 2:00 PM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Practical vocational skills and career exploration for future employment.",
    fullDescription: "The Career Technology program introduces students to various vocational skills and career paths. Students explore technical skills, entrepreneurship, and career planning.",
    objectives: [
      "Explore various career pathways",
      "Develop practical technical skills",
      "Understand entrepreneurship basics",
      "Learn workplace safety and ethics",
      "Create career development plans",
      "Build foundational vocational skills"
    ],
    activities: [
      "Hands-on technical projects",
      "Career exploration visits",
      "Entrepreneurship workshops",
      "Mini-business projects",
      "Skills demonstrations",
      "Career counseling sessions"
    ],
    materials: ["Career tech textbooks", "Tools and equipment", "Safety gear", "Project materials", "Career guides"],
    rating: 4.7,
    enrolled: 90,
    resourceLinks: [
      { name: "Resource 1", url: "#jhs-career-1" },
      { name: "Resource 2", url: "#jhs-career-2" },
      { name: "Resource 3", url: "#jhs-career-3" },
      { name: "Resource 4", url: "#jhs-career-4" },
      { name: "Resource 5", url: "#jhs-career-5" },
      { name: "Resource 6", url: "#jhs-career-6" },
      { name: "Resource 7", url: "#jhs-career-7" },
      { name: "Resource 8", url: "#jhs-career-8" },
      { name: "Resource 9", url: "#jhs-career-9" },
      { name: "Resource 10", url: "#jhs-career-10" },
      { name: "Homework 1", url: "#jhs-career-hw-1" },
      { name: "Homework 2", url: "#jhs-career-hw-2" },
      { name: "Homework 3", url: "#jhs-career-hw-3" },
      { name: "Homework 4", url: "#jhs-career-hw-4" },
      { name: "Homework 5", url: "#jhs-career-hw-5" },
      { name: "Homework 6", url: "#jhs-career-hw-6" },
      { name: "Homework 7", url: "#jhs-career-hw-7" },
      { name: "Homework 8", url: "#jhs-career-hw-8" },
      { name: "Homework 9", url: "#jhs-career-hw-9" },
      { name: "Homework 10", url: "#jhs-career-hw-10" },
    ]
  },
  french: {
    name: "French",
    color: "from-[#002147] to-[#003366]",
    icon: "🇫🇷",
    room: "Language Laboratory",
    schedule: "Tue/Thu 9:00 AM",
    teacher: "Mme. Claire Mensah",
    teacherImage: "",
    description: "Intermediate French proficiency for BECE success and international communication.",
    fullDescription: "The JHS French program develops intermediate proficiency in French. Students advance their speaking, listening, reading, and writing skills for BECE excellence and future opportunities.",
    objectives: [
      "Achieve intermediate French proficiency",
      "Master French grammar and vocabulary",
      "Communicate confidently in French",
      "Read and comprehend French texts",
      "Write coherent French compositions",
      "Excel in BECE French examinations"
    ],
    activities: [
      "Immersive conversation practice",
      "French film and media analysis",
      "Writing workshops",
      "Cultural exchange activities",
      "BECE oral exam preparation",
      "French language competitions"
    ],
    materials: ["JHS French textbooks", "Audio-visual materials", "French literature", "Language lab software", "BECE prep materials"],
    rating: 4.7,
    enrolled: 90,
    resourceLinks: [
      { name: "Resource 1", url: "#jhs-french-1" },
      { name: "Resource 2", url: "#jhs-french-2" },
      { name: "Resource 3", url: "#jhs-french-3" },
      { name: "Resource 4", url: "#jhs-french-4" },
      { name: "Resource 5", url: "#jhs-french-5" },
      { name: "Resource 6", url: "#jhs-french-6" },
      { name: "Resource 7", url: "#jhs-french-7" },
      { name: "Resource 8", url: "#jhs-french-8" },
      { name: "Resource 9", url: "#jhs-french-9" },
      { name: "Resource 10", url: "#jhs-french-10" },
      { name: "Homework 1", url: "#jhs-french-hw-1" },
      { name: "Homework 2", url: "#jhs-french-hw-2" },
      { name: "Homework 3", url: "#jhs-french-hw-3" },
      { name: "Homework 4", url: "#jhs-french-hw-4" },
      { name: "Homework 5", url: "#jhs-french-hw-5" },
      { name: "Homework 6", url: "#jhs-french-hw-6" },
      { name: "Homework 7", url: "#jhs-french-hw-7" },
      { name: "Homework 8", url: "#jhs-french-hw-8" },
      { name: "Homework 9", url: "#jhs-french-hw-9" },
      { name: "Homework 10", url: "#jhs-french-hw-10" },
    ]
  },
  fante: {
    name: "Fante",
    color: "from-[#002147] to-[#003366]",
    icon: "🇬🇭",
    room: "JHS Block, Room 105",
    schedule: "Mon/Wed/Fri 2:00 PM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Advanced Fante language skills and cultural literacy for BECE success.",
    fullDescription: "The JHS Fante program develops advanced proficiency in the Fante language. Students master reading, writing, and speaking while exploring rich cultural traditions.",
    objectives: [
      "Achieve fluent Fante communication",
      "Master Fante orthography and grammar",
      "Interpret proverbs and idioms deeply",
      "Analyze Fante literature and poetry",
      "Appreciate cultural practices and values",
      "Excel in BECE Ghanaian Language"
    ],
    activities: [
      "Advanced conversation practice",
      "Literary analysis sessions",
      "Proverb interpretation workshops",
      "Cultural storytelling",
      "Writing competitions",
      "BECE preparation classes"
    ],
    materials: ["JHS Fante textbooks", "Literature anthologies", "Audio materials", "Cultural resources", "BECE prep books"],
    rating: 4.8,
    enrolled: 90,
    resourceLinks: [
      { name: "Resource 1", url: "#jhs-fante-1" },
      { name: "Resource 2", url: "#jhs-fante-2" },
      { name: "Resource 3", url: "#jhs-fante-3" },
      { name: "Resource 4", url: "#jhs-fante-4" },
      { name: "Resource 5", url: "#jhs-fante-5" },
      { name: "Resource 6", url: "#jhs-fante-6" },
      { name: "Resource 7", url: "#jhs-fante-7" },
      { name: "Resource 8", url: "#jhs-fante-8" },
      { name: "Resource 9", url: "#jhs-fante-9" },
      { name: "Resource 10", url: "#jhs-fante-10" },
      { name: "Homework 1", url: "#jhs-fante-hw-1" },
      { name: "Homework 2", url: "#jhs-fante-hw-2" },
      { name: "Homework 3", url: "#jhs-fante-hw-3" },
      { name: "Homework 4", url: "#jhs-fante-hw-4" },
      { name: "Homework 5", url: "#jhs-fante-hw-5" },
      { name: "Homework 6", url: "#jhs-fante-hw-6" },
      { name: "Homework 7", url: "#jhs-fante-hw-7" },
      { name: "Homework 8", url: "#jhs-fante-hw-8" },
      { name: "Homework 9", url: "#jhs-fante-hw-9" },
      { name: "Homework 10", url: "#jhs-fante-hw-10" },
    ]
  },
  "computer-science": {
    name: "Computer Science, Coding and Engineering",
    color: "from-[#002147] to-[#003366]",
    icon: "💻",
    room: "ICT Laboratory",
    schedule: "Mon/Wed/Fri 1:00 PM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Advanced computing, programming, and engineering for the digital future.",
    fullDescription: "The JHS Computer Science program develops advanced digital skills. Students learn programming, web development, and engineering principles for future technology careers.",
    objectives: [
      "Master computer applications and productivity tools",
      "Learn programming fundamentals and logic",
      "Understand computer networks and internet",
      "Develop problem-solving through coding",
      "Explore engineering design processes",
      "Build projects using technology"
    ],
    activities: [
      "Programming projects",
      "Web development basics",
      "Robotics and electronics",
      "App development introduction",
      "Digital citizenship training",
      "Tech fair participation"
    ],
    materials: ["Computers and laptops", "Programming software", "Robotics kits", "ICT textbooks", "Online resources"],
    rating: 4.9,
    enrolled: 90,
    resourceLinks: [
      { name: "Resource 1", url: "#jhs-ict-1" },
      { name: "Resource 2", url: "#jhs-ict-2" },
      { name: "Resource 3", url: "#jhs-ict-3" },
      { name: "Resource 4", url: "#jhs-ict-4" },
      { name: "Resource 5", url: "#jhs-ict-5" },
      { name: "Resource 6", url: "#jhs-ict-6" },
      { name: "Resource 7", url: "#jhs-ict-7" },
      { name: "Resource 8", url: "#jhs-ict-8" },
      { name: "Resource 9", url: "#jhs-ict-9" },
      { name: "Resource 10", url: "#jhs-ict-10" },
      { name: "Homework 1", url: "#jhs-ict-hw-1" },
      { name: "Homework 2", url: "#jhs-ict-hw-2" },
      { name: "Homework 3", url: "#jhs-ict-hw-3" },
      { name: "Homework 4", url: "#jhs-ict-hw-4" },
      { name: "Homework 5", url: "#jhs-ict-hw-5" },
      { name: "Homework 6", url: "#jhs-ict-hw-6" },
      { name: "Homework 7", url: "#jhs-ict-hw-7" },
      { name: "Homework 8", url: "#jhs-ict-hw-8" },
      { name: "Homework 9", url: "#jhs-ict-hw-9" },
      { name: "Homework 10", url: "#jhs-ict-hw-10" },
    ]
  }
};

// JHS Subject Page Component
export const JHSSubjectPage = () => {
  const navigate = useNavigate();
  const { subject } = useParams();
  
  const subjectData = jhsSubjectsData[subject];
  
  if (!subjectData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#002147] to-[#003366] flex items-center justify-center">
        <Card className="bg-white p-8 text-center max-w-md">
          <h2 className="text-2xl font-bold text-[#002147] mb-4">Subject Not Found</h2>
          <p className="text-gray-600 mb-6">The subject you're looking for doesn't exist.</p>
          <Button 
            onClick={() => navigate("/course-catalog")}
            className="bg-[#FF6B00] hover:bg-[#E65C00]"
          >
            <ArrowLeft size={16} className="mr-2" /> Back to Course Catalog
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#002147] to-[#003366]" data-testid={`jhs-subject-${subject}`}>
      {/* Hero Section */}
      <div className={`bg-gradient-to-r ${subjectData.color} text-white py-12`}>
        <div className="max-w-5xl mx-auto px-4">
          <Button 
            className="mb-4 bg-gradient-to-b from-white/30 to-white/10 hover:from-white/40 hover:to-white/20 text-white border-2 border-white/50 transition-all rounded-full shadow-[0_4px_0_0_rgba(0,0,0,0.3)] hover:shadow-[0_2px_0_0_rgba(0,0,0,0.3)] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] font-medium"
            onClick={() => navigate("/course-catalog")}
            data-testid="back-to-courses-btn"
          >
            <ArrowLeft size={18} className="mr-2" /> Back to Course Catalog
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center text-4xl">
              {subjectData.icon}
            </div>
            <div>
              <Badge className="bg-white/20 text-white mb-2">JHS (Junior High School)</Badge>
              <h1 className="text-3xl md:text-4xl font-bold">{subjectData.name}</h1>
              <p className="text-white/90 mt-2 text-lg">{subjectData.description}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Resource & Homework Buttons Section */}
      {subjectData.resourceLinks && subjectData.resourceLinks.length > 0 && (
        <div className="bg-gradient-to-r from-pink-400 via-pink-300 to-pink-400 py-6">
          <div className="max-w-6xl mx-auto px-4">
            {/* Row 1: Resource buttons */}
            <div className="flex flex-wrap justify-center gap-2 mb-3">
              {subjectData.resourceLinks.filter(r => r.name.startsWith('Resource')).map((resource, index) => {
                const buttonColors = [
                  { bg: 'linear-gradient(145deg, #ff6b6b, #ee5a24)', shadow: '#c0392b' },
                  { bg: 'linear-gradient(145deg, #ffa502, #e67e22)', shadow: '#d35400' },
                  { bg: 'linear-gradient(145deg, #7bed9f, #2ed573)', shadow: '#27ae60' },
                  { bg: 'linear-gradient(145deg, #70a1ff, #1e90ff)', shadow: '#0c5bb5' },
                  { bg: 'linear-gradient(145deg, #a29bfe, #6c5ce7)', shadow: '#4834d4' },
                  { bg: 'linear-gradient(145deg, #fd79a8, #e84393)', shadow: '#b83280' },
                  { bg: 'linear-gradient(145deg, #fdcb6e, #f39c12)', shadow: '#d68910' },
                  { bg: 'linear-gradient(145deg, #55efc4, #00b894)', shadow: '#00856a' },
                  { bg: 'linear-gradient(145deg, #74b9ff, #0984e3)', shadow: '#0767b1' },
                  { bg: 'linear-gradient(145deg, #dfe6e9, #b2bec3)', shadow: '#808e95' },
                ];
                const colorSet = buttonColors[index % buttonColors.length];
                const isPlaceholder = resource.url.startsWith('#');
                return (
                  <a
                    key={index}
                    href={isPlaceholder ? undefined : resource.url}
                    target={isPlaceholder ? undefined : "_blank"}
                    rel={isPlaceholder ? undefined : "noopener noreferrer"}
                    onClick={isPlaceholder ? (e) => e.preventDefault() : undefined}
                    className="inline-block px-4 py-2 text-xs font-bold text-white rounded-lg cursor-pointer transition-all duration-200 hover:brightness-110 active:translate-y-[3px] active:shadow-none no-underline"
                    style={{
                      background: colorSet.bg,
                      boxShadow: `0 5px 0 ${colorSet.shadow}, 0 8px 15px rgba(0,0,0,0.2)`,
                      transform: 'translateY(-2px)',
                    }}
                    onMouseDown={(e) => { e.currentTarget.style.transform = 'translateY(1px)'; e.currentTarget.style.boxShadow = `0 2px 0 ${colorSet.shadow}, 0 3px 5px rgba(0,0,0,0.15)`; }}
                    onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 5px 0 ${colorSet.shadow}, 0 8px 15px rgba(0,0,0,0.2)`; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 5px 0 ${colorSet.shadow}, 0 8px 15px rgba(0,0,0,0.2)`; }}
                    data-testid={`resource-btn-${index + 1}`}
                  >
                    {resource.name}
                  </a>
                );
              })}
            </div>
            {/* Row 2: Homework buttons */}
            <div className="flex flex-wrap justify-center gap-2">
              {subjectData.resourceLinks.filter(r => r.name.startsWith('Homework')).map((resource, index) => {
                const buttonColors = [
                  { bg: 'linear-gradient(145deg, #ff6b6b, #ee5a24)', shadow: '#c0392b' },
                  { bg: 'linear-gradient(145deg, #ffa502, #e67e22)', shadow: '#d35400' },
                  { bg: 'linear-gradient(145deg, #7bed9f, #2ed573)', shadow: '#27ae60' },
                  { bg: 'linear-gradient(145deg, #70a1ff, #1e90ff)', shadow: '#0c5bb5' },
                  { bg: 'linear-gradient(145deg, #a29bfe, #6c5ce7)', shadow: '#4834d4' },
                  { bg: 'linear-gradient(145deg, #fd79a8, #e84393)', shadow: '#b83280' },
                  { bg: 'linear-gradient(145deg, #fdcb6e, #f39c12)', shadow: '#d68910' },
                  { bg: 'linear-gradient(145deg, #55efc4, #00b894)', shadow: '#00856a' },
                  { bg: 'linear-gradient(145deg, #74b9ff, #0984e3)', shadow: '#0767b1' },
                  { bg: 'linear-gradient(145deg, #dfe6e9, #b2bec3)', shadow: '#808e95' },
                ];
                const colorSet = buttonColors[index % buttonColors.length];
                const isPlaceholder = resource.url.startsWith('#');
                return (
                  <a
                    key={index}
                    href={isPlaceholder ? undefined : resource.url}
                    target={isPlaceholder ? undefined : "_blank"}
                    rel={isPlaceholder ? undefined : "noopener noreferrer"}
                    onClick={isPlaceholder ? (e) => e.preventDefault() : undefined}
                    className="inline-block px-4 py-2 text-xs font-bold text-white rounded-lg cursor-pointer transition-all duration-200 hover:brightness-110 active:translate-y-[3px] active:shadow-none no-underline"
                    style={{
                      background: colorSet.bg,
                      boxShadow: `0 5px 0 ${colorSet.shadow}, 0 8px 15px rgba(0,0,0,0.2)`,
                      transform: 'translateY(-2px)',
                    }}
                    onMouseDown={(e) => { e.currentTarget.style.transform = 'translateY(1px)'; e.currentTarget.style.boxShadow = `0 2px 0 ${colorSet.shadow}, 0 3px 5px rgba(0,0,0,0.15)`; }}
                    onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 5px 0 ${colorSet.shadow}, 0 8px 15px rgba(0,0,0,0.2)`; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 5px 0 ${colorSet.shadow}, 0 8px 15px rgba(0,0,0,0.2)`; }}
                    data-testid={`homework-btn-${index + 1}`}
                  >
                    {resource.name}
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Content Section */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-6">
            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#002147]">
                  <BookOpen size={20} /> About This Course
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">{subjectData.fullDescription}</p>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#002147]">
                  <Target size={20} /> Learning Objectives
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {subjectData.objectives.map((objective, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-[#002147] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle size={14} className="text-white" />
                      </div>
                      <span className="text-gray-700">{objective}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#002147]">
                  <Lightbulb size={20} /> Learning Activities
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-3">
                  {subjectData.activities.map((activity, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                      <div className="w-2 h-2 bg-[#002147] rounded-full"></div>
                      <span className="text-gray-700 text-sm">{activity}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card className="bg-white shadow-lg">
              <CardHeader className="bg-[#002147] text-white rounded-t-lg">
                <CardTitle className="text-lg">Course Instructor</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="text-center">
                  {subjectData.teacherImage ? (
                    <img src={subjectData.teacherImage} alt={subjectData.teacher} className="w-24 h-24 rounded-full mx-auto border-4 border-[#002147] object-cover" />
                  ) : (
                    <div className="w-24 h-24 rounded-full mx-auto border-4 border-blue-200 bg-blue-50 flex items-center justify-center">
                      <UserCircle size={40} className="text-blue-300" />
                    </div>
                  )}
                  <h3 className="font-semibold text-[#002147] mt-3">{subjectData.teacher}</h3>
                  <div className="flex items-center justify-center gap-1 text-yellow-500 mt-1">
                    <Star size={14} fill="currentColor" />
                    <span className="text-sm font-medium">{subjectData.rating} rating</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg text-[#002147]">Course Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Clock size={18} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Schedule</p>
                    <p className="font-medium text-gray-800">{subjectData.schedule}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <MapPin size={18} className="text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Location</p>
                    <p className="font-medium text-gray-800">{subjectData.room}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Users size={18} className="text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Enrolled</p>
                    <p className="font-medium text-gray-800">{subjectData.enrolled} Students</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#002147]/10 rounded-lg flex items-center justify-center">
                    <GraduationCap size={18} className="text-[#002147]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Level</p>
                    <p className="font-medium text-gray-800">JHS (Junior High School)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Button 
              className="w-full bg-[#002147] hover:bg-[#003366]"
              onClick={() => navigate("/portal")}
            >
              <User size={16} className="mr-2" /> Contact Teacher
            </Button>

            {/* Exams Prep Buttons */}
            <Card className="bg-white shadow-lg">
              <CardHeader className="bg-gradient-to-r from-[#FF6B00] to-[#e65c00] text-white rounded-t-lg">
                <CardTitle className="text-lg text-center">Exams Prep</CardTitle>
              </CardHeader>
              <CardContent className="p-3">
                <div className="flex flex-col gap-2">
                  {Array.from({ length: 15 }, (_, i) => {
                    const prepColors = [
                      { bg: 'linear-gradient(145deg, #ff6b6b, #ee5a24)', shadow: '#c0392b' },
                      { bg: 'linear-gradient(145deg, #ffa502, #e67e22)', shadow: '#d35400' },
                      { bg: 'linear-gradient(145deg, #7bed9f, #2ed573)', shadow: '#27ae60' },
                      { bg: 'linear-gradient(145deg, #70a1ff, #1e90ff)', shadow: '#0c5bb5' },
                      { bg: 'linear-gradient(145deg, #a29bfe, #6c5ce7)', shadow: '#4834d4' },
                      { bg: 'linear-gradient(145deg, #fd79a8, #e84393)', shadow: '#b83280' },
                      { bg: 'linear-gradient(145deg, #fdcb6e, #f39c12)', shadow: '#d68910' },
                      { bg: 'linear-gradient(145deg, #55efc4, #00b894)', shadow: '#00856a' },
                      { bg: 'linear-gradient(145deg, #74b9ff, #0984e3)', shadow: '#0767b1' },
                      { bg: 'linear-gradient(145deg, #dfe6e9, #b2bec3)', shadow: '#808e95' },
                      { bg: 'linear-gradient(145deg, #e17055, #d63031)', shadow: '#b71c1c' },
                      { bg: 'linear-gradient(145deg, #00cec9, #0097a7)', shadow: '#00796b' },
                      { bg: 'linear-gradient(145deg, #6c5ce7, #5f27cd)', shadow: '#4a148c' },
                      { bg: 'linear-gradient(145deg, #fab1a0, #e17055)', shadow: '#c0392b' },
                      { bg: 'linear-gradient(145deg, #81ecec, #00b894)', shadow: '#00796b' },
                    ];
                    const colorSet = prepColors[i];
                    return (
                      <a
                        key={i}
                        href={`/jhs-subject/${subject}/exam-prep/${i + 1}`}
                        onClick={(e) => { e.preventDefault(); navigate(`/jhs-subject/${subject}/exam-prep/${i + 1}`); }}
                        className="block w-full text-center px-3 py-2 text-xs font-bold text-white rounded-lg cursor-pointer transition-all duration-200 hover:brightness-110 active:translate-y-[2px] active:shadow-none no-underline"
                        style={{
                          background: colorSet.bg,
                          boxShadow: `0 4px 0 ${colorSet.shadow}, 0 6px 10px rgba(0,0,0,0.2)`,
                          transform: 'translateY(-2px)',
                        }}
                        onMouseDown={(e) => { e.currentTarget.style.transform = 'translateY(0px)'; e.currentTarget.style.boxShadow = `0 1px 0 ${colorSet.shadow}, 0 2px 4px rgba(0,0,0,0.15)`; }}
                        onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 4px 0 ${colorSet.shadow}, 0 6px 10px rgba(0,0,0,0.2)`; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 4px 0 ${colorSet.shadow}, 0 6px 10px rgba(0,0,0,0.2)`; }}
                        data-testid={`exam-prep-btn-${i + 1}`}
                      >
                        Exams Prep {i + 1}
                      </a>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* Bottom Back Button */}
        <div className="mt-8 text-center">
          <Button 
            className="bg-gradient-to-b from-[#002147] to-[#001a38] hover:from-[#003366] hover:to-[#002147] text-white transition-all rounded-full shadow-[0_4px_0_0_#000814] hover:shadow-[0_2px_0_0_#000814] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] font-medium px-8"
            onClick={() => navigate("/course-catalog")}
            data-testid="back-to-courses-btn-bottom"
          >
            <ArrowLeft size={18} className="mr-2" /> Back to Course Catalog
          </Button>
        </div>
      </div>
    </div>
  );
};

export default JHSSubjectPage;
